import { createCanvasEditingDispatch } from "./editing-dispatch.js";
import { parseExternalPromptText, type PromptRange } from "./prompts/prompt-edit-operations.js";
import { plainPrompt, promptToPlainText, applyCanvasReferenceCommandV3, type PromptContent } from "@seedance/contracts";
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ChangeEvent,
  type DragEvent as ReactDragEvent,
  type PointerEvent as ReactPointerEvent
} from "react";
import { CanvasRecoveryNotice } from "./CanvasRecoveryNotice.js";
import {
  applyEdgeChanges,
  applyNodeChanges,
  Background,
  BackgroundVariant,
  MiniMap,
  ReactFlow,
  SelectionMode,
  type Connection,
  type Edge,
  type EdgeChange,
  type Node,
  type NodeChange,
  type NodeTypes,
  type ReactFlowInstance
} from "@xyflow/react";
import "@xyflow/react/dist/style.css";
import type {
  AssetView,
  AssetVisibility,
  CanvasDocumentV3 as CanvasDocument,
  CanvasViewV3 as CanvasView,
  ProjectView,
  PromptTemplateView,
  SeedanceCapabilitiesView,
  SeedanceNodeSettingsV3 as SeedanceNodeSettings
} from "@seedance/contracts";
import type { BuildPreviewResult } from "@seedance/workflow";
import { useNavigate, useParams } from "react-router";
import { ApiClientError, apiClient, type WorkflowView } from "../api/client.js";
import { createUploadTransport } from "../api/upload-transport.js";
import { useAuth } from "../auth/AuthProvider.js";
import { canvasCopy, seedanceModelLabel } from "../copy/zh-CN.js";
import {
  ProductionLibraryDialog,
  type ProductionLibraryTab
} from "../library/ProductionLibraryDialog.js";
import { canvasSaveStatusLabel, shouldPersistCanvasViewChange } from "./view-state.js";
import { resolveInsertionPoint } from "./insertion-point.js";
import { generationPickerPosition } from "./generation-picker-position.js";
import { createGenerationSubmissionAttemptController } from "./generation-submission.js";
import {
  appendPlacedAssets,
  canvasPointAtViewportCenter,
  clientPointToCanvas,
  createTouchRetryQueue,
  normalizePendingAssetInput,
  placeLibraryAssets,
  type Point
} from "./asset-drop.js";
import { hydrateCanvasAssets, mergeHydratedAssets } from "./asset-hydration.js";
import { canvasGroupBounds } from "./group-overlay.js";
import { CanvasGroupOverlay } from "./CanvasGroupOverlay.js";
import { useRenderProjection } from "./model/use-render-projection.js";
import { shouldPersistEdgeChanges, shouldPersistNodeChanges } from "./model/flow-changes.js";
import {
  canvasDocumentToFlow,
  decorateSelectionEdges,
  flowDiffersFromDocument,
  flowToCanvasDocument,
  mergeCanvasDocumentIntoFlow,
  withFlowViewport,
  type CanvasFlowEdgeData,
  type CanvasFlowNodeData
} from "./model/xyflow-adapter.js";
import {
  applyCanvasCommand,
  createCanvasHistory,
  redoCanvas,
  undoCanvas,
  type CanvasHistoryState
} from "./model/history.js";
import { productionNodeTypes } from "./nodes/node-types.js";
import type { CanvasNodeKind } from "./nodes/node-types.js";
import {
  openCanvasMenu,
  openCanvasMenuAt,
  openEdgeMenu,
  openNodeMenu,
  type CanvasMenuState
} from "./context-menu.js";
import {
  readDesktopSidebarPreference,
  writeDesktopSidebarPreference,
  type DesktopSidebar
} from "./sidebar-preference.js";
import {
  createCanvasConnections,
  createCanvasConnection,
  createSeedanceGeneration,
  createSeedanceGenerationFromSources,
  isCanvasInputNode,
  resolveConnectionSources
} from "./connection-actions.js";
import { CanvasContextMenu } from "./menus/CanvasContextMenu.js";
import { BatchConnectionPreview } from "./BatchConnectionPreview.js";
import { SelectionConnectionOverlay } from "./SelectionConnectionOverlay.js";
import { selectionConnectorLayout } from "./selection-connector.js";
import { resolveSelectionConnectionDrop } from "./selection-connection-drop.js";
import { CanvasToolRail } from "./CanvasToolRail.js";
import { GenerationAssetHistoryDialog } from "./panels/GenerationAssetHistoryDialog.js";
import { canvasHistoryShortcut, canvasTools, type CanvasToolId } from "./canvas-tools.js";
import { SeedanceNode } from "./nodes/SeedanceNode.js";
import { ComposerFlowNode } from "./nodes/ComposerFlowNode.js";
import {
  AssetPanel,
  assetDragMimeType,
  createAssetPanelController,
  readAssetDragData
} from "./panels/AssetPanel.js";
import {
  UploadQueue,
  createUploadQueueController
} from "./panels/UploadQueue.js";
import { TextNodeInspector } from "./panels/TextNodeInspector.js";
import { useRequestPreviewBuilder } from "./model/use-request-preview-builder.js";
import {
  updateSeedanceSettings,
  updateTextNode
} from "./seedance-editor.js";
import { copyCanvasSelection } from "./clipboard.js";
import { latestWorkflowId, mergeWorkflows } from "./workflow-selection.js";
import { mergeWorkflowInsertion } from "./workflow-insertion.js";
import { useCanvasSession } from "./useCanvasSession.js";
import { MobileNodeList } from "./panels/MobileNodeList.js";
import {
  createAssetReferenceSearchController,
  groupComposerCandidates,
  type AssetReferenceSearchState
} from "./composer/asset-reference-search.js";
import { SeedanceComposer } from "./composer/SeedanceComposer.js";
import { composerLayout } from "./composer/composer-layout.js";
import { SeedanceComposerOverlay } from "./composer/SeedanceComposerOverlay.js";
import {
  composerPlacementToFlowNode,
  defaultComposerPlacement
} from "./composer/composer-position.js";
import type { MentionQuery } from "./composer/mention-query.js";
import { addComposerReferenceWithPrompt } from "./composer/reference-actions.js";
import { CanvasToolbar } from "./toolbar/CanvasToolbar.js";
import { NodeActionStrip } from "./toolbar/NodeActionStrip.js";
import { SelectionToolbar } from "./toolbar/SelectionToolbar.js";
import {
  alignNodes,
  collapseNodes,
  collapsedNodeProjection,
  deleteNodes,
  distributeNodes,
  duplicateNodes,
  groupNodes,
  moveGroupNodes,
  ungroupNodes,
  type Alignment,
  type DistributionAxis
} from "./toolbar/canvas-commands.js";

const canvasNodeTypes = {
  ...productionNodeTypes,
  seedance: SeedanceNode,
  composer: ComposerFlowNode
} satisfies NodeTypes;

type PageState =
  | { status: "loading" }
  | { status: "ready"; project: ProjectView; canvas: CanvasView }
  | { status: "not-found" }
  | { status: "offline" }
  | { status: "error" };

interface GroupDragState {
  groupId: string;
  pointerId: number;
  startClient: Point;
  initialDocument: CanvasDocument;
  zoom: number;
}

interface GenerationPickerState {
  sourceNodeIds?: string[];
  client: Point;
  flow: Point;
}

function CanvasEditor({
  project,
  canvas,
  ownerId,
  viewerRole
}: {
  project: ProjectView;
  canvas: CanvasView;
  ownerId: string;
  viewerRole: "member" | "system_admin";
}) {
  const navigate = useNavigate();
  const canvasElementRef = useRef<HTMLElement | null>(null);
  const session = useCanvasSession({
    ownerId,
    projectId: project.id,
    projectName: project.name,
    initialCanvas: canvas,
    navigate
  });
  const generationSubmissionAttempts = useMemo(
    () => createGenerationSubmissionAttemptController(),
    []
  );
  const [flow, setFlow] = useState(() => canvasDocumentToFlow(session.document));
  const flowRef = useRef(flow);
  const documentRef = useRef(session.document);
  const groupDragRef = useRef<GroupDragState | undefined>(undefined);
  const historyRef = useRef<CanvasHistoryState>(createCanvasHistory(session.document));
  const [history, setHistory] = useState(historyRef.current);
  const [flowInstance, setFlowInstance] = useState<ReactFlowInstance<Node<CanvasFlowNodeData>, Edge<CanvasFlowEdgeData>> | null>(null);
  const [search, setSearch] = useState("");
  const [phonePreview, setPhonePreview] = useState(false);
  const [productionLibraryOpen, setProductionLibraryOpen] = useState(false);
  const [productionLibraryInitialTab, setProductionLibraryInitialTab] = useState<ProductionLibraryTab>("assets");
  const [generationHistoryOpen, setGenerationHistoryOpen] = useState(false);
  const [tabletDrawer, setTabletDrawer] = useState<"assets" | "inspector" | undefined>(undefined);
  const [desktopSidebars, setDesktopSidebars] = useState(() =>
    readDesktopSidebarPreference(typeof window === "undefined" ? undefined : window.localStorage)
  );
  const [assetsById, setAssetsById] = useState<Record<string, AssetView>>({});
  const [capabilities, setCapabilities] = useState<
    | { status: "loading" }
    | { status: "ready"; value: SeedanceCapabilitiesView }
    | { status: "error" }
  >({ status: "loading" });
  const [isDragActive, setIsDragActive] = useState(false);
  const [touchFailures, setTouchFailures] = useState<AssetView[]>([]);
  const [menu, setMenu] = useState<CanvasMenuState | undefined>(undefined);
  const [generationPicker, setGenerationPicker] = useState<GenerationPickerState | undefined>(undefined);
  const [isConnecting, setIsConnecting] = useState(false);
  const [batchConnectionPreview, setBatchConnectionPreview] = useState<
    | { sourceNodeIds: string[]; client: Point }
    | undefined
  >(undefined);
  const [canvasTool, setCanvasTool] = useState<"select" | "pan">("select");
  const [activeCanvasToolPopover, setActiveCanvasToolPopover] = useState<CanvasToolId | undefined>(undefined);
  const [uploadVisibility, setUploadVisibility] = useState<AssetVisibility>("private");
  const [bulkSelectionMode, setBulkSelectionMode] = useState(false);
  const [quickMediaAdd, setQuickMediaAdd] = useState<
    | { kind: "image" | "video" | "audio"; point: Point }
    | undefined
  >(undefined);
  const [workflows, setWorkflows] = useState<WorkflowView[]>([]);
  const [selectedWorkflowId, setSelectedWorkflowId] = useState<string | undefined>(undefined);
  const [workflowStatus, setWorkflowStatus] = useState<string | undefined>(undefined);
  const [workflowInserting, setWorkflowInserting] = useState(false);
  const [composerSearchState, setComposerSearchState] = useState<AssetReferenceSearchState>({
    query: "",
    assets: [],
    nextCursor: undefined,
    status: "idle"
  });
  const [composerVisibleNodeId, setComposerVisibleNodeId] = useState<string | undefined>(undefined);
  const [canvasViewportSize, setCanvasViewportSize] = useState({ width: 1200, height: 700 });
  const [composerViewportSize, setComposerViewportSize] = useState(() => ({
    width: window.visualViewport?.width ?? window.innerWidth,
    height: window.visualViewport?.height ?? window.innerHeight
  }));
  const quickMediaInputRef = useRef<HTMLInputElement>(null);
  const uploadVisibilityRef = useRef<AssetVisibility>(uploadVisibility);
  const assetSearchInputRef = useRef<HTMLInputElement>(null);
  const connectionStartRef = useRef<string[] | undefined>(undefined);
  const selectionConnectionDragRef = useRef<{ pointerId: number; sourceNodeIds: string[] } | undefined>(undefined);
  const bulkSelectionModeRef = useRef(false);
  const lastInsertionPointRef = useRef<
    | { point: Point; origin: "canvas" | "automatic" }
    | undefined
  >(undefined);
  const changeDocument = session.change;

  uploadVisibilityRef.current = uploadVisibility;

  useEffect(() => {
    writeDesktopSidebarPreference(
      typeof window === "undefined" ? undefined : window.localStorage,
      desktopSidebars
    );
  }, [desktopSidebars]);

  const toggleDesktopSidebar = useCallback((sidebar: DesktopSidebar) => {
    setDesktopSidebars((current) => ({ ...current, [sidebar]: !current[sidebar] }));
  }, []);

  const rememberAssets = useCallback((assets: readonly AssetView[]) => {
    setAssetsById((current) => mergeHydratedAssets(current, assets));
  }, []);
  const hydratedAssetCache = useMemo(
    () => new Map(Object.entries(assetsById)),
    [assetsById]
  );
  useEffect(() => {
    let active = true;
    void hydrateCanvasAssets({
      document: session.document,
      cachedAssets: hydratedAssetCache,
      getAsset: apiClient.getAsset.bind(apiClient)
    }).then((assets) => {
      if (active) rememberAssets(assets);
    });
    return () => {
      active = false;
    };
  }, [hydratedAssetCache, rememberAssets, session.document]);

  const touchRetries = useMemo(
    () =>
      createTouchRetryQueue({
        touchAsset: apiClient.touchAsset.bind(apiClient),
        onSuccess: (asset) => rememberAssets([asset])
      }),
    [rememberAssets]
  );
  useEffect(() => touchRetries.subscribe(setTouchFailures), [touchRetries]);

  const commitDocument = useCallback(
    (document: CanvasDocument) => {
      // Content editors consume a graph snapshot. Capture the live viewport at
      // commit time (including asynchronous uploads), without mutating callers.
      // Undo/redo and server/draft restores deliberately use their own viewport.
      const normalizedDocument = { ...document, viewport: flowRef.current.viewport };
      const nextHistory = applyCanvasCommand(historyRef.current, {
        type: "replace_document",
        document: normalizedDocument
      });
      historyRef.current = nextHistory;
      setHistory(nextHistory);
      documentRef.current = normalizedDocument;
      setFlow((previous) => {
        const next = mergeCanvasDocumentIntoFlow(normalizedDocument, previous);
        flowRef.current = next;
        return next;
      });
      changeDocument(normalizedDocument);
    },
    [changeDocument]
  );
  const commitDocumentRef = useRef(commitDocument);
  useEffect(() => {
    commitDocumentRef.current = commitDocument;
  }, [commitDocument]);

  const viewportCenter = useCallback((): Point | undefined => {
    const element = canvasElementRef.current;
    if (!element) return undefined;
    const bounds = element.getBoundingClientRect();
    return canvasPointAtViewportCenter({
      bounds: { width: bounds.width, height: bounds.height },
      viewport: flow.viewport
    });
  }, [flow.viewport]);
  const resolveCanvasInsertionPoint = useCallback((
    canvasPoint?: Point,
    nodeSize: { width: number; height: number } = { width: 360, height: 240 },
    preserveCanvasPoint = true
  ): Point | undefined => {
    const element = canvasElementRef.current;
    const bounds = element?.getBoundingClientRect();
    const visibleNodeBounds = bounds
      ? {
          x: -flow.viewport.x / flow.viewport.zoom,
          y: -flow.viewport.y / flow.viewport.zoom,
          width: bounds.width / flow.viewport.zoom,
          height: bounds.height / flow.viewport.zoom
        }
      : undefined;
    const center = viewportCenter();
    const centeredViewportPoint = center
      ? { x: center.x - nodeSize.width / 2, y: center.y - nodeSize.height / 2 }
      : undefined;
    // `flowRef` is updated immediately at the end of a drag, whereas the
    // persisted document can still be one render behind. New cards must
    // avoid the positions the user can currently see.
    const occupiedNodeBounds = flowRef.current.nodes
      .filter((node) => node.type !== "composer")
      .map((node) => ({
        x: node.position.x,
        y: node.position.y,
        width: node.width ?? node.data.canvasNode.size.width,
        height: node.height ?? node.data.canvasNode.size.height
      }));
    const rememberedPoint = lastInsertionPointRef.current;
    const rememberedCanvasPointIsOccupied = rememberedPoint?.origin === "canvas" &&
      occupiedNodeBounds.some((occupied) => (
        rememberedPoint.point.x < occupied.x + occupied.width &&
        rememberedPoint.point.x + nodeSize.width > occupied.x &&
        rememberedPoint.point.y < occupied.y + occupied.height &&
        rememberedPoint.point.y + nodeSize.height > occupied.y
      ));
    const rememberedCanvasPoint = preserveCanvasPoint ? canvasPoint ?? (
      rememberedPoint?.origin === "canvas" && !rememberedCanvasPointIsOccupied
        ? rememberedPoint.point
        : undefined
    ) : undefined;
    const candidatePoint = canvasPoint ?? rememberedPoint?.point;
    const candidatePointMayAvoidCollisions = candidatePoint !== undefined;
    const lastPointFitsViewport = candidatePointMayAvoidCollisions && (
      visibleNodeBounds === undefined || (
        candidatePoint.x >= visibleNodeBounds.x &&
        candidatePoint.y >= visibleNodeBounds.y &&
        candidatePoint.x + nodeSize.width <= visibleNodeBounds.x + visibleNodeBounds.width &&
        candidatePoint.y + nodeSize.height <= visibleNodeBounds.y + visibleNodeBounds.height
      )
    );
    const point = resolveInsertionPoint({
      ...(rememberedCanvasPoint ? { explicitCanvasPoint: rememberedCanvasPoint } : {}),
      lastCanvasPoint: lastPointFitsViewport ? candidatePoint : undefined,
      viewportCenter: centeredViewportPoint,
      nodeSize,
      ...(visibleNodeBounds ? { visibleNodeBounds } : {}),
      occupiedNodeBounds
    });
    if (point) {
      lastInsertionPointRef.current = {
        point: { ...point },
        origin: rememberedCanvasPoint ? "canvas" : "automatic"
      };
    }
    return point;
  }, [flow.viewport, viewportCenter]);

  useEffect(() => {
    if (documentRef.current !== session.document) {
      historyRef.current = createCanvasHistory(session.document);
      setHistory(historyRef.current);
    }
    documentRef.current = session.document;
    setFlow((previous) => {
      const next = mergeCanvasDocumentIntoFlow(session.document, previous);
      flowRef.current = next;
      return next;
    });
  }, [session.document]);

  useEffect(() => {
    let active = true;
    void apiClient.getSeedanceCapabilities().then(
      (value) => {
        if (active) setCapabilities({ status: "ready", value });
      },
      () => {
        if (active) setCapabilities({ status: "error" });
      }
    );
    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    let active = true;
    void apiClient.listWorkflows().then(
      (value) => {
        if (active) setWorkflows((current) => mergeWorkflows(current, value));
      },
      () => {
        if (active) setWorkflowStatus(canvasCopy.workflowLoadFailed);
      }
    );
    return () => {
      active = false;
    };
  }, []);
  useEffect(() => {
    setSelectedWorkflowId((current) =>
      current && workflows.some(({ id }) => id === current)
        ? current
        : latestWorkflowId(workflows)
    );
  }, [workflows]);

  const uploadAsset = useMemo(
    () =>
      createUploadTransport({
        csrfToken: () => apiClient.getCsrfToken()
      }),
    []
  );
  const uploadQueue = useMemo(
    () =>
      createUploadQueueController({
        uploadAsset,
        projectId: project.id,
        visibility: () => uploadVisibilityRef.current,
        onComplete(asset, canvasPoint) {
          rememberAssets([asset]);
          const placement = appendPlacedAssets(
            documentRef.current,
            [asset],
            canvasPoint
          );
          commitDocumentRef.current(placement);
          if (asset.visibility === "team") {
            void apiClient.publishSharedAsset({
              assetId: asset.id,
              title: asset.displayName,
              description: ""
            }).then(
              () => setWorkflowStatus("素材已共享到团队资料库。"),
              () => setWorkflowStatus("素材已上传，但共享失败；可在生产资料库中重新共享。")
            );
          }
        }
      }),
    [project.id, rememberAssets, uploadAsset]
  );
  const uploadQueueMounted = useRef(false);
  useEffect(() => {
    uploadQueueMounted.current = true;
    return () => {
      uploadQueueMounted.current = false;
      queueMicrotask(() => {
        if (!uploadQueueMounted.current) uploadQueue.dispose();
      });
    };
  }, [uploadQueue]);

  const assetPanel = useMemo(
    () =>
      createAssetPanelController({
        client: apiClient,
        onAssets: rememberAssets
      }),
    [rememberAssets]
  );
  const acceptImportedAsset = useCallback((asset: AssetView) => {
    rememberAssets([asset]);
    assetPanel.setView("library");
    void assetPanel.load();
  }, [assetPanel, rememberAssets]);
  const acceptImportedPrompt = useCallback((_template: PromptTemplateView) => {
    // Prompt templates remain in the private template library. They are only
    // applied to a composer after a separate, explicit user action.
  }, []);
  const savePromptTemplate = useCallback(async (input: { title: string; content: string }) => {
    await apiClient.createPromptTemplate(input);
  }, []);
  const submitSeedanceGeneration = useCallback(async (_preview: BuildPreviewResult, seedanceNodeId: string) => {
    try {
      const mutationVersion = session.getMutationVersion();
      const job = await session.runWithSavedRevision((canvasRevision) => (
        generationSubmissionAttempts.submit({
          projectId: project.id,
          canvasRevision,
          targetNodeId: seedanceNodeId,
          mutationVersion
        }, (commandId) => apiClient.createGeneration({
          projectId: project.id,
          canvasRevision,
          targetNodeId: seedanceNodeId,
          commandId
        }))
      ));
      if (!job) throw new Error("画布尚未保存，生成任务没有提交；请先处理顶部保存提示。");
      return `生成任务已提交，当前状态：${job.state}。`;
    } catch (error) {
      if (error instanceof ApiClientError && error.code === "GENERATION_SUBMISSION_DISABLED") {
        throw new Error("生成服务尚未启用；当前配置已保存在画布中。");
      }
      if (error instanceof ApiClientError && error.status === 409) {
        throw new Error("画布版本已更新，请等待保存完成后重试。");
      }
      if (error instanceof ApiClientError) {
        throw new Error(`生成任务提交失败（${error.code}，请求 ${error.requestId}）。`);
      }
      throw new Error("网络响应中断，任务可能已受理；再次提交会安全确认，不会重复创建。");
    }
  }, [generationSubmissionAttempts, project.id, session]);
  const openProductionLibrary = useCallback((tab: ProductionLibraryTab = "assets") => {
    setProductionLibraryInitialTab(tab);
    setProductionLibraryOpen(true);
  }, []);
  const composerAssetSearch = useMemo(
    () => createAssetReferenceSearchController({ listAssets: apiClient.listAssets.bind(apiClient) }),
    []
  );
  const refreshComposerSearchState = useCallback(() => {
    setComposerSearchState(composerAssetSearch.getState());
  }, [composerAssetSearch]);
  const searchComposerAssets = useCallback((query: string) => {
    void composerAssetSearch.search(query).finally(refreshComposerSearchState);
    refreshComposerSearchState();
  }, [composerAssetSearch, refreshComposerSearchState]);
  const loadMoreComposerAssets = useCallback(() => {
    void composerAssetSearch.loadMore().finally(refreshComposerSearchState);
    refreshComposerSearchState();
  }, [composerAssetSearch, refreshComposerSearchState]);
  useEffect(() => {
    const updateCanvasViewport = () => {
      const element = canvasElementRef.current;
      if (!element) return;
      const next = { width: element.clientWidth, height: element.clientHeight };
      setCanvasViewportSize((current) =>
        current.width === next.width && current.height === next.height ? current : next
      );
    };
    const updateLayout = () => {
      updateCanvasViewport();
      setComposerViewportSize({
        width: window.visualViewport?.width ?? window.innerWidth,
        height: window.visualViewport?.height ?? window.innerHeight
      });
    };
    updateLayout();
    const canvasElement = canvasElementRef.current;
    let canvasResizeObserver: ResizeObserver | undefined;
    if (typeof ResizeObserver !== "undefined" && canvasElement) {
      canvasResizeObserver = new ResizeObserver(updateCanvasViewport);
      canvasResizeObserver.observe(canvasElement);
    }
    window.addEventListener("resize", updateLayout);
    window.visualViewport?.addEventListener("resize", updateLayout);
    return () => {
      canvasResizeObserver?.disconnect();
      window.removeEventListener("resize", updateLayout);
      window.visualViewport?.removeEventListener("resize", updateLayout);
    };
  }, []);

  const updateFlow = useCallback(
    (nextFlow: typeof flow, persist = true) => {
      flowRef.current = nextFlow;
      setFlow(nextFlow);
      if (persist && flowDiffersFromDocument(nextFlow, documentRef.current)) {
        const document = flowToCanvasDocument(nextFlow, documentRef.current);
        documentRef.current = document;
        commitDocument(document);
      }
    },
    [commitDocument]
  );
  const onNodesChange = useCallback(
    (changes: NodeChange[]) => {
      const current = flowRef.current;
      const effectiveChanges = bulkSelectionModeRef.current
        ? changes.filter((change) => change.type !== "select")
        : changes;
      if (effectiveChanges.length === 0) return;
      const next = {
        ...current,
        nodes: applyNodeChanges(effectiveChanges, current.nodes) as typeof current.nodes
      };
      updateFlow(next, shouldPersistNodeChanges(effectiveChanges));
    },
    [updateFlow]
  );
  const onEdgesChange = useCallback(
    (changes: EdgeChange[]) => {
      const current = flowRef.current;
      updateFlow({
        ...current,
        edges: applyEdgeChanges(changes, current.edges) as typeof current.edges
      }, shouldPersistEdgeChanges(changes));
    },
    [updateFlow]
  );
  const connectionSourceIds = useCallback((startedNodeId: string) =>
    resolveConnectionSources({
      nodes: documentRef.current.nodes,
      selectedNodeIds: flowRef.current.nodes
        .filter((node) => node.selected)
        .map((node) => node.id),
      startedNodeId
    }).map((node) => node.id),
  []);
  const connectSourcesToSeedance = useCallback((sourceIds: readonly string[], targetNodeId: string) => {
    const current = flowRef.current;
    const targetNode = documentRef.current.nodes.find(({ id }) => id === targetNodeId);
    if (targetNode?.kind !== "seedance") return false;
    const sourcesById = new Map(documentRef.current.nodes.map((node) => [node.id, node]));
    const canvasEdges = createCanvasConnections({
      sources: sourceIds.flatMap((id) => {
        const node = sourcesById.get(id);
        return node && isCanvasInputNode(node) ? [node] : [];
      }),
      target: targetNode,
      existingEdges: documentRef.current.edges,
      createId: () => crypto.randomUUID()
    });
    if (canvasEdges.length === 0) return false;
    updateFlow({
      ...current,
      nodes: current.nodes.map((node) => ({ ...node, selected: node.id === targetNode.id })),
      edges: [
        ...current.edges,
        ...canvasEdges.map((canvasEdge) => ({
          id: canvasEdge.id,
          source: canvasEdge.sourceNodeId,
          target: canvasEdge.targetNodeId,
          data: { canvasEdge }
        }))
      ]
    });
    return true;
  }, [updateFlow]);
  const isPreviewingBatchConnection = batchConnectionPreview !== undefined;
  useEffect(() => {
    if (!isPreviewingBatchConnection) return;
    const move = (event: PointerEvent) => {
      setBatchConnectionPreview((current) => current === undefined
        ? current
        : { ...current, client: { x: event.clientX, y: event.clientY } });
    };
    window.addEventListener("pointermove", move);
    return () => window.removeEventListener("pointermove", move);
  }, [isPreviewingBatchConnection]);
  const onConnect = useCallback(
    (connection: Connection) => {
      if (!connection.source || !connection.target) return;
      const sourceIds = connectionStartRef.current ?? connectionSourceIds(connection.source);
      setBatchConnectionPreview(undefined);
      connectSourcesToSeedance(sourceIds, connection.target);
    },
    [connectSourcesToSeedance, connectionSourceIds]
  );
  const editingDispatch = useMemo(() => createCanvasEditingDispatch({getDocument:()=>documentRef.current,change:commitDocument}),[commitDocument]);
  const deleteEdgesById = editingDispatch.deleteEdges;
  useEffect(() => {
    const onDeleteSelectedEdges = (event: KeyboardEvent) => {
      const target = event.target;
      const editing = target instanceof HTMLInputElement || target instanceof HTMLTextAreaElement || target instanceof HTMLSelectElement || (target instanceof HTMLElement && target.isContentEditable);
      editingDispatch.deleteKey({key:event.key,defaultPrevented:event.defaultPrevented,editing,edgeIds:flowRef.current.edges.filter(edge=>edge.selected).map(edge=>edge.id),preventDefault:()=>event.preventDefault()});
    };
    window.addEventListener("keydown", onDeleteSelectedEdges);
    return () => window.removeEventListener("keydown", onDeleteSelectedEdges);
  }, [editingDispatch]);
  useEffect(() => {
    const onCanvasModeShortcut = (event: KeyboardEvent) => {
      if (event.defaultPrevented || event.ctrlKey || event.metaKey || event.altKey) return;
      const target = event.target;
      if (
        target instanceof HTMLInputElement ||
        target instanceof HTMLTextAreaElement ||
        target instanceof HTMLSelectElement ||
        (target instanceof HTMLElement && target.isContentEditable)
      ) return;
      const key = event.key.toLowerCase();
      if (key !== "v" && key !== "h") return;
      event.preventDefault();
      setCanvasTool(key === "v" ? "select" : "pan");
    };
    window.addEventListener("keydown", onCanvasModeShortcut);
    return () => window.removeEventListener("keydown", onCanvasModeShortcut);
  }, []);
  const updateInlineTextNode = useCallback((nodeId: string, text: PromptContent) => {
    commitDocument(updateTextNode(documentRef.current, nodeId, text));
  }, [commitDocument]);
  const addTextNodeAt = useCallback((canvasPoint?: Point) => {
    const point = resolveCanvasInsertionPoint(canvasPoint, { width: 240, height: 120 });
    if (!point) return;
    const id = crypto.randomUUID();
    const history = applyCanvasCommand(createCanvasHistory(documentRef.current), {
      type: "add_node",
      node: {
        id,
        kind: "text",
        position: point,
        size: { width: 240, height: 120 },
        collapsed: false,
        text: plainPrompt("新建笔记")
      }
    });
    commitDocument(history.present);
  }, [commitDocument, resolveCanvasInsertionPoint]);

  const addTextNode = useCallback(() => addTextNodeAt(), [addTextNodeAt]);

  const addScriptNodeAt = useCallback((point: Point) => {
    const history = applyCanvasCommand(createCanvasHistory(documentRef.current), {
      type: "add_node",
      node: {
        id: crypto.randomUUID(),
        kind: "script",
        position: point,
        size: { width: 300, height: 180 },
        collapsed: false,
        title: plainPrompt("手动脚本"),
        synopsis: plainPrompt("")
      }
    });
    commitDocument(history.present);
  }, [commitDocument]);

  const addScriptNode = useCallback(() => {
    const point = resolveCanvasInsertionPoint(undefined, { width: 300, height: 180 });
    if (point) addScriptNodeAt(point);
  }, [addScriptNodeAt, resolveCanvasInsertionPoint]);

  const undo = useCallback(() => {
    const next = undoCanvas(historyRef.current);
    if (next === historyRef.current) return;
    historyRef.current = next;
    setHistory(next);
    documentRef.current = next.present;
    setFlow((previous) => {
      const flow = mergeCanvasDocumentIntoFlow(next.present, previous);
      flowRef.current = flow;
      return flow;
    });
    changeDocument(next.present);
  }, [changeDocument]);

  const redo = useCallback(() => {
    const next = redoCanvas(historyRef.current);
    if (next === historyRef.current) return;
    historyRef.current = next;
    setHistory(next);
    documentRef.current = next.present;
    setFlow((previous) => {
      const flow = mergeCanvasDocumentIntoFlow(next.present, previous);
      flowRef.current = flow;
      return flow;
    });
    changeDocument(next.present);
  }, [changeDocument]);

  useEffect(() => {
    const onCanvasHistoryShortcut = (event: KeyboardEvent) => {
      if (event.defaultPrevented || event.altKey) return;
      const target = event.target;
      const editable = target instanceof HTMLInputElement ||
        target instanceof HTMLTextAreaElement ||
        target instanceof HTMLSelectElement ||
        (target instanceof HTMLElement && target.isContentEditable);
      const action = canvasHistoryShortcut({
        key: event.key,
        ctrlKey: event.ctrlKey,
        metaKey: event.metaKey,
        shiftKey: event.shiftKey,
        editable
      });
      if (!action) return;
      event.preventDefault();
      if (action === "undo") undo();
      else redo();
    };
    window.addEventListener("keydown", onCanvasHistoryShortcut);
    return () => window.removeEventListener("keydown", onCanvasHistoryShortcut);
  }, [redo, undo]);

  const openSeedancePicker = useCallback((canvasPoint?: Point, sourceNodeIds?: readonly string[]) => {
    if (capabilities.status !== "ready") return;
    const point = resolveCanvasInsertionPoint(canvasPoint);
    if (!point) return;
    const bounds = canvasElementRef.current?.getBoundingClientRect();
    const client = bounds && flowInstance
      ? {
          x: bounds.left + flow.viewport.x + point.x * flow.viewport.zoom,
          y: bounds.top + flow.viewport.y + point.y * flow.viewport.zoom
        }
      : { x: 64, y: 96 };
    setGenerationPicker({
      ...(sourceNodeIds && sourceNodeIds.length > 0 ? { sourceNodeIds: [...sourceNodeIds] } : {}),
      client,
      flow: point
    });
  }, [capabilities.status, flow.viewport, flowInstance, resolveCanvasInsertionPoint]);

  const createSeedanceFromPicker = useCallback((model: SeedanceNodeSettings["model"]) => {
    const picker = generationPicker;
    if (!picker || capabilities.status !== "ready") return;
    const sourceIds = picker.sourceNodeIds ?? [];
    const sourcesById = new Map(documentRef.current.nodes.map((node) => [node.id, node]));
    const sources = sourceIds.flatMap((id) => {
      const node = sourcesById.get(id);
      return node ? [node] : [];
    });
    if (sources.length !== sourceIds.length) {
      setGenerationPicker(undefined);
      return;
    }
    const created = createSeedanceGenerationFromSources({
      sources,
      point: picker.flow,
      capabilities: capabilities.value,
      model,
      // Web Crypto methods need their `crypto` receiver in Chromium. Passing the
      // method itself makes a picker selection fail with `Illegal invocation`.
      createId: () => crypto.randomUUID()
    });
    if (!created) return;
    const nextDocument = {
      ...documentRef.current,
      nodes: [...documentRef.current.nodes, created.node],
      edges: [...documentRef.current.edges, ...created.edges]
    };
    commitDocument(nextDocument);
    setBulkSelectionMode(false);
    bulkSelectionModeRef.current = false;
    setComposerVisibleNodeId(created.node.id);
    setFlow((current) => {
      const selected = {
        ...current,
        nodes: current.nodes.map((node) => ({ ...node, selected: node.id === created.node.id }))
      };
      flowRef.current = selected;
      return selected;
    });
    setGenerationPicker(undefined);
  }, [capabilities, commitDocument, generationPicker]);

  const chooseFiles = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      const files = Array.from(event.currentTarget.files ?? []);
      const point = resolveCanvasInsertionPoint();
      if (files.length > 0 && point) uploadQueue.enqueue(files, point);
      event.currentTarget.value = "";
    },
    [resolveCanvasInsertionPoint, uploadQueue]
  );

  const chooseQuickMedia = useCallback((event: ChangeEvent<HTMLInputElement>) => {
    const target = quickMediaAdd;
    const files = Array.from(event.currentTarget.files ?? []);
    event.currentTarget.value = "";
    setQuickMediaAdd(undefined);
    if (!target || files.length === 0) return;
    uploadQueue.enqueue(files, target.point);
  }, [quickMediaAdd, uploadQueue]);

  const createQuickNode = useCallback((kind: CanvasNodeKind, canvasPoint?: Point) => {
    const point = resolveCanvasInsertionPoint(canvasPoint);
    if (!point) return;
    if (kind === "text") {
      addTextNodeAt(point);
      return;
    }
    if (kind === "script") {
      addScriptNodeAt(point);
      return;
    }
    if (kind === "seedance") {
      openSeedancePicker(point);
      return;
    }
    setQuickMediaAdd({ kind, point });
    queueMicrotask(() => quickMediaInputRef.current?.click());
  }, [addScriptNodeAt, addTextNodeAt, openSeedancePicker, resolveCanvasInsertionPoint]);

  const placePanelAsset = useCallback(
    (asset: AssetView, canvasPoint?: Point) => {
      const point = resolveCanvasInsertionPoint(canvasPoint);
      if (!point) return;
      const input = normalizePendingAssetInput({
        source: "library",
        assets: [asset],
        canvasPoint: point
      });
      rememberAssets(input.assets);
      const next = placeLibraryAssets({
        document: documentRef.current,
        assets: input.assets,
        anchor: input.canvasPoint,
        touchAsset: apiClient.touchAsset.bind(apiClient),
        onTouchFailure: touchRetries.add
      });
      commitDocument(next);
    },
    [commitDocument, rememberAssets, resolveCanvasInsertionPoint, touchRetries]
  );

  const onDrop = useCallback(
    (event: ReactDragEvent<HTMLElement>) => {
      event.preventDefault();
      setIsDragActive(false);
      const element = canvasElementRef.current;
      if (!element) return;
      const point = clientPointToCanvas({
        clientPoint: { x: event.clientX, y: event.clientY },
        bounds: element.getBoundingClientRect(),
        viewport: flow.viewport
      });
      const insertionPoint = resolveCanvasInsertionPoint(point);
      if (!insertionPoint) return;
      const libraryAsset = readAssetDragData(event.dataTransfer);
      if (libraryAsset) {
        placePanelAsset(libraryAsset, insertionPoint);
        return;
      }
      const files = Array.from(event.dataTransfer.files);
      if (files.length > 0) {
        const input = normalizePendingAssetInput({
          source: "drop",
          files,
          canvasPoint: insertionPoint
        });
        uploadQueue.enqueue(input.files, input.canvasPoint);
      }
    },
    [flow.viewport, placePanelAsset, resolveCanvasInsertionPoint, uploadQueue]
  );

  const onDragOver = useCallback((event: ReactDragEvent<HTMLElement>) => {
    if (
      event.dataTransfer.files.length > 0 ||
      Array.from(event.dataTransfer.types).includes("Files") ||
      Array.from(event.dataTransfer.types).includes(assetDragMimeType)
    ) {
      event.preventDefault();
      event.dataTransfer.dropEffect = "copy";
      setIsDragActive(true);
    }
  }, []);

  useEffect(() => {
    const paste = (event: ClipboardEvent) => {
      const target = event.target;
      if (
        target instanceof HTMLElement &&
        (target.isContentEditable || target.matches("input, textarea, select"))
      ) {
        return;
      }
      const files = Array.from(event.clipboardData?.files ?? []);
      const point = resolveCanvasInsertionPoint();
      if (files.length === 0 || !point) return;
      event.preventDefault();
      const input = normalizePendingAssetInput({
        source: "paste",
        files,
        canvasPoint: point
      });
      uploadQueue.enqueue(input.files, input.canvasPoint);
    };
    window.addEventListener("paste", paste);
    return () => window.removeEventListener("paste", paste);
  }, [resolveCanvasInsertionPoint, uploadQueue]);

  // Live viewport updates do not change the editable graph. Keep geometry fresh
  // during real drags, but use the persisted viewport for this editing snapshot.
  // Screen positioning continues to read flow.viewport; local commits capture it.
  const editorFlow = useMemo(() => ({
    schemaVersion: flow.schemaVersion,
    nodes: flow.nodes,
    edges: flow.edges,
    groups: flow.groups,
    viewport: session.document.viewport
  }), [flow.schemaVersion, flow.nodes, flow.edges, flow.groups, session.document.viewport]);
  const editorDocument = useMemo(() => flowToCanvasDocument(editorFlow), [editorFlow]);
  const requestPreviewBuilder = useRequestPreviewBuilder(editorDocument);
  const selectedIds = useMemo(
    () => flow.nodes.filter((node) => node.selected).map((node) => node.id),
    [flow.nodes]
  );
  const effectiveSelectedIds = selectedIds;
  const selectedCollapsedCount = useMemo(
    () => editorDocument.nodes.filter((node) => effectiveSelectedIds.includes(node.id) && node.collapsed).length,
    [editorDocument.nodes, effectiveSelectedIds]
  );
  const selectNode = useCallback((nodeId: string) => {
    setFlow((current) => {
      const next = {
        ...current,
        nodes: current.nodes.map((node) => ({ ...node, selected: node.id === nodeId }))
      };
      flowRef.current = next;
      return next;
    });
  }, []);
  const selectGroup = useCallback((groupId: string) => {
    const group = documentRef.current.groups.find(({ id }) => id === groupId);
    if (!group) return;
    const members = new Set(group.nodeIds);
    setFlow((current) => {
      const next = {
        ...current,
        nodes: current.nodes.map((node) => ({ ...node, selected: members.has(node.id) }))
      };
      flowRef.current = next;
      return next;
    });
  }, []);
  const previewGroupDocument = useCallback((document: CanvasDocument) => {
    const positions = new Map(document.nodes.map((node) => [node.id, node.position] as const));
    setFlow((current) => {
      const next = {
        ...current,
        nodes: current.nodes.map((node) => {
          const position = positions.get(node.id);
          return position ? { ...node, position } : node;
        })
      };
      flowRef.current = next;
      return next;
    });
  }, []);
  const groupDragDelta = useCallback((event: ReactPointerEvent<HTMLButtonElement>, drag: GroupDragState) => ({
    x: (event.clientX - drag.startClient.x) / drag.zoom,
    y: (event.clientY - drag.startClient.y) / drag.zoom
  }), []);
  const startGroupDrag = useCallback((event: ReactPointerEvent<HTMLButtonElement>, groupId: string) => {
    if (event.button !== 0) return;
    const initialDocument = documentRef.current;
    if (!initialDocument.groups.some(({ id }) => id === groupId)) return;
    event.preventDefault();
    event.currentTarget.setPointerCapture(event.pointerId);
    selectGroup(groupId);
    groupDragRef.current = {
      groupId,
      pointerId: event.pointerId,
      startClient: { x: event.clientX, y: event.clientY },
      initialDocument,
      zoom: flowRef.current.viewport.zoom
    };
  }, [selectGroup]);
  const moveGroupDrag = useCallback((event: ReactPointerEvent<HTMLButtonElement>) => {
    const drag = groupDragRef.current;
    if (!drag || drag.pointerId !== event.pointerId) return;
    previewGroupDocument(moveGroupNodes(drag.initialDocument, drag.groupId, groupDragDelta(event, drag)));
  }, [groupDragDelta, previewGroupDocument]);
  const finishGroupDrag = useCallback((event: ReactPointerEvent<HTMLButtonElement>) => {
    const drag = groupDragRef.current;
    if (!drag || drag.pointerId !== event.pointerId) return;
    groupDragRef.current = undefined;
    if (event.currentTarget.hasPointerCapture(event.pointerId)) event.currentTarget.releasePointerCapture(event.pointerId);
    const document = moveGroupNodes(drag.initialDocument, drag.groupId, groupDragDelta(event, drag));
    if (document === drag.initialDocument) {
      previewGroupDocument(drag.initialDocument);
      return;
    }
    commitDocument(document);
  }, [commitDocument, groupDragDelta, previewGroupDocument]);
  const cancelGroupDrag = useCallback((event: ReactPointerEvent<HTMLButtonElement>) => {
    const drag = groupDragRef.current;
    if (!drag || drag.pointerId !== event.pointerId) return;
    groupDragRef.current = undefined;
    if (event.currentTarget.hasPointerCapture(event.pointerId)) event.currentTarget.releasePointerCapture(event.pointerId);
    previewGroupDocument(drag.initialDocument);
  }, [previewGroupDocument]);
  const ungroupGroup = useCallback((groupId: string) => {
    const document = ungroupNodes(documentRef.current, [groupId]);
    if (document !== documentRef.current) commitDocument(document);
  }, [commitDocument]);
  const applySelectionCommand = useCallback(
    (command: (document: CanvasDocument, ids: readonly string[]) => CanvasDocument) => {
      const next = command(documentRef.current, selectedIds);
      if (next !== documentRef.current) commitDocument(next);
    },
    [commitDocument, selectedIds]
  );
  const locateSearchResult = useCallback(() => {
    const query = search.trim().toLocaleLowerCase();
    if (!query) return;
    const node = flow.nodes.find(({ data }) => {
      const canvasNode = data.canvasNode;
      const label = canvasNode.kind === "text"
        ? promptToPlainText(canvasNode.text)
        : canvasNode.kind === "seedance"
          ? promptToPlainText(canvasNode.settings.prompt)
          : canvasNode.kind === "script"
            ? `${promptToPlainText(canvasNode.title)} ${promptToPlainText(canvasNode.synopsis)}`
            : `${canvasNode.kind} ${canvasNode.assetId}`;
      return label.toLocaleLowerCase().includes(query);
    });
    if (!node) return;
    selectNode(node.id);
    void flowInstance?.setCenter(
      node.position.x + (node.width ?? 0) / 2,
      node.position.y + (node.height ?? 0) / 2,
      { zoom: Math.max(flow.viewport.zoom, 0.9), duration: 180 }
    );
  }, [flow.nodes, flow.viewport.zoom, flowInstance, search, selectNode]);
  const availableAssets = useMemo(() => Object.values(assetsById), [assetsById]);
  const selectedMediaNodes = useMemo(
    () => editorDocument.nodes.filter((node): node is Extract<CanvasDocument["nodes"][number], { kind: "image" | "video" | "audio" }> =>
      effectiveSelectedIds.includes(node.id) && (node.kind === "image" || node.kind === "video" || node.kind === "audio")
    ),
    [editorDocument.nodes, effectiveSelectedIds]
  );
  const selectedMediaIds = useMemo(() => selectedMediaNodes.map((node) => node.id), [selectedMediaNodes]);
  const selectedInputNodes = useMemo(
    () => editorDocument.nodes.filter((node) => effectiveSelectedIds.includes(node.id) && isCanvasInputNode(node)),
    [editorDocument.nodes, effectiveSelectedIds]
  );
  const selectedInputIds = useMemo(() => selectedInputNodes.map((node) => node.id), [selectedInputNodes]);
  const selectionConnector = useMemo(() => {
    const selected = new Set(selectedInputIds);
    return selectionConnectorLayout({
      nodes: flow.nodes.flatMap((node) => {
        if (!selected.has(node.id) || !isCanvasInputNode(node.data.canvasNode)) return [];
        return [{
          id: node.id,
          position: node.position,
          width: node.measured?.width ?? node.width ?? node.data.canvasNode.size.width,
          height: node.measured?.height ?? node.height ?? node.data.canvasNode.size.height
        }];
      }),
      viewport: flow.viewport
    });
  }, [flow.nodes, flow.viewport, selectedInputIds]);
  const seedanceTargets = useMemo(
    () => editorDocument.nodes
      .filter((node): node is Extract<CanvasDocument["nodes"][number], { kind: "seedance" }> => node.kind === "seedance")
      .map((node) => ({ id: node.id, label: seedanceModelLabel(node.settings.model) })),
    [editorDocument.nodes]
  );
  const toggleBulkSelection = useCallback(() => {
    const nextBulkSelectionMode = !bulkSelectionModeRef.current;
    bulkSelectionModeRef.current = nextBulkSelectionMode;
    setBulkSelectionMode(nextBulkSelectionMode);
    if (nextBulkSelectionMode) {
      setCanvasTool("select");
      setComposerVisibleNodeId(undefined);
    }
    setFlow((current) => {
      const next = {
        ...current,
        nodes: current.nodes.map((node) => ({ ...node, selected: false }))
      };
      flowRef.current = next;
      return next;
    });
  }, []);
  const beginSelectionConnection = useCallback((event: ReactPointerEvent<HTMLButtonElement>) => {
    if (selectedInputIds.length === 0) return;
    selectionConnectionDragRef.current = {
      pointerId: event.pointerId,
      sourceNodeIds: [...selectedInputIds]
    };
    event.currentTarget.setPointerCapture(event.pointerId);
    setIsConnecting(true);
    setBatchConnectionPreview({
      sourceNodeIds: [...selectedInputIds],
      client: { x: event.clientX, y: event.clientY }
    });
  }, [selectedInputIds]);
  const clearSelectionConnection = useCallback((pointerId: number) => {
    const drag = selectionConnectionDragRef.current;
    if (!drag || drag.pointerId !== pointerId) return;
    selectionConnectionDragRef.current = undefined;
    setIsConnecting(false);
    setBatchConnectionPreview(undefined);
  }, []);
  const finishSelectionConnectionAt = useCallback((pointerId: number, client: Point) => {
    const drag = selectionConnectionDragRef.current;
    if (!drag || drag.pointerId !== pointerId || !flowInstance) return;
    selectionConnectionDragRef.current = undefined;
    setIsConnecting(false);
    setBatchConnectionPreview(undefined);
    const element = document.elementFromPoint(client.x, client.y);
    const targetNodeId = element?.closest<HTMLElement>(".react-flow__node[data-id]")?.dataset.id;
    const droppedOnPane = Boolean(element?.closest(".react-flow__pane")) && !element?.closest(
      ".react-flow__node, .studio-upload-queue, .studio-selection-toolbar, .studio-batch-connection-hint, .studio-selection-connector"
    );
    const action = resolveSelectionConnectionDrop({
      sourceNodeIds: drag.sourceNodeIds,
      droppedOnPane,
      nodes: documentRef.current.nodes,
      ...(targetNodeId === undefined ? {} : { targetNodeId })
    });
    if (action?.kind === "connect") {
      connectSourcesToSeedance(drag.sourceNodeIds, action.targetNodeId);
      return;
    }
    if (action?.kind === "generate") {
      const point = resolveCanvasInsertionPoint(flowInstance.screenToFlowPosition(client));
      if (!point) return;
      setGenerationPicker({
        sourceNodeIds: drag.sourceNodeIds,
        client,
        flow: point
      });
    }
  }, [connectSourcesToSeedance, flowInstance, resolveCanvasInsertionPoint]);
  const cancelSelectionConnection = useCallback((event: ReactPointerEvent<HTMLButtonElement>) => {
    if (event.currentTarget.hasPointerCapture(event.pointerId)) event.currentTarget.releasePointerCapture(event.pointerId);
    clearSelectionConnection(event.pointerId);
  }, [clearSelectionConnection]);
  const finishSelectionConnection = useCallback((event: ReactPointerEvent<HTMLButtonElement>) => {
    if (event.currentTarget.hasPointerCapture(event.pointerId)) event.currentTarget.releasePointerCapture(event.pointerId);
    finishSelectionConnectionAt(event.pointerId, { x: event.clientX, y: event.clientY });
  }, [finishSelectionConnectionAt]);
  useEffect(() => {
    const finish = (event: PointerEvent) => {
      finishSelectionConnectionAt(event.pointerId, { x: event.clientX, y: event.clientY });
    };
    const finishMouse = (event: MouseEvent) => {
      const pointerId = selectionConnectionDragRef.current?.pointerId;
      if (pointerId !== undefined) finishSelectionConnectionAt(pointerId, { x: event.clientX, y: event.clientY });
    };
    const cancel = (event: PointerEvent) => clearSelectionConnection(event.pointerId);
    window.addEventListener("pointerup", finish);
    window.addEventListener("mouseup", finishMouse);
    window.addEventListener("pointercancel", cancel);
    return () => {
      window.removeEventListener("pointerup", finish);
      window.removeEventListener("mouseup", finishMouse);
      window.removeEventListener("pointercancel", cancel);
    };
  }, [clearSelectionConnection, finishSelectionConnectionAt]);
  const openSelectionSeedancePicker = useCallback(() => {
    if (selectedInputIds.length > 0) openSeedancePicker(undefined, selectedInputIds);
  }, [openSeedancePicker, selectedInputIds]);
  const createBulkSeedance = useCallback(() => {
    if (selectedMediaIds.length < 2) return;
    openSeedancePicker(undefined, selectedMediaIds);
  }, [openSeedancePicker, selectedMediaIds]);
  const connectBulkToSeedance = useCallback((targetNodeId: string) => {
    if (selectedMediaNodes.length < 2) return;
    const target = documentRef.current.nodes.find((node) => node.id === targetNodeId);
    if (target?.kind !== "seedance") return;
    const edges = createCanvasConnections({
      sources: selectedMediaNodes,
      target,
      existingEdges: documentRef.current.edges,
      createId: () => crypto.randomUUID()
    });
    if (edges.length === 0) return;
    const nextDocument = { ...documentRef.current, edges: [...documentRef.current.edges, ...edges] };
    commitDocument(nextDocument);
    setFlow((current) => {
      const selected = {
        ...current,
        nodes: current.nodes.map((node) => ({ ...node, selected: node.id === target.id }))
      };
      flowRef.current = selected;
      return selected;
    });
    setBulkSelectionMode(false);
    bulkSelectionModeRef.current = false;
  }, [commitDocument, selectedMediaNodes]);
  const displayFlow = useMemo(() => {
    const { nodes, edges } = decorateSelectionEdges(editorFlow, {
      bulkSourceNodeIds: selectedMediaNodes.length > 1 ? selectedMediaIds : []
    });
    return { nodes, edges };
  }, [editorFlow, selectedMediaIds, selectedMediaNodes.length]);
  const renderedNodes = useMemo(
    () =>
      displayFlow.nodes.map((node) => {
        const canvasNode = node.data.canvasNode;
        if (
          canvasNode.kind === "image" ||
          canvasNode.kind === "video" ||
          canvasNode.kind === "audio"
        ) {
          return {
            ...node,
            zIndex: 2,
            ...collapsedNodeProjection(canvasNode),
            type: canvasNode.kind,
            data: {
              ...node.data,
              refreshHandles: true,
              asset: assetsById[canvasNode.assetId]
            }
          };
        }
        if (canvasNode.kind === "seedance") {
          const references = requestPreviewBuilder.resolve(canvasNode.id).references;
          const referenceCounts = {
            image: references.filter(({ kind }) => kind === "image").length,
            video: references.filter(({ kind }) => kind === "video").length,
            audio: references.filter(({ kind }) => kind === "audio").length
          };
          return {
            ...node,
            zIndex: 2,
            ...collapsedNodeProjection(canvasNode),
            type: "seedance",
            data: {
              ...node.data,
              refreshHandles: true,
              canvasNode,
              referenceCounts,
              ...(capabilities.status === "ready"
                ? {
                    preview: requestPreviewBuilder.build({
                      seedanceNode: canvasNode,
                      capabilities: capabilities.value,
                      assets: availableAssets
                    })
                  }
                : {})
            }
          };
        }
        return {
          ...node,
          zIndex: 2,
          ...collapsedNodeProjection(canvasNode),
          type: canvasNode.kind === "text" ? "text" : canvasNode.kind === "script" ? "script" : "default",
          data: {
            ...node.data,
            refreshHandles: true,
            ...(canvasNode.kind === "text" || canvasNode.kind === "script" ? { document:editorDocument, onDocumentChange:commitDocument, onUndo:undo, onRedo:redo } : {})
          }
        };
      }) as typeof flow.nodes,
    [availableAssets, assetsById, capabilities, displayFlow.nodes, editorDocument, requestPreviewBuilder, commitDocument, undo, redo]
  );
  const renderProjection = useRenderProjection(renderedNodes, displayFlow.edges);

  const selectedSeedanceNode = useMemo(() => {
    const selectedId = flow.nodes.find(({ selected }) => selected)?.id;
    const node = editorDocument.nodes.find(({ id }) => id === selectedId);
    return node?.kind === "seedance" ? node : undefined;
  }, [editorDocument.nodes, flow.nodes]);
  useEffect(() => {
    const selectedNodeId = flow.nodes.find(({ selected }) => selected)?.id;
    if (!selectedNodeId) return;
    if (selectedSeedanceNode) {
      setComposerVisibleNodeId(selectedSeedanceNode.id);
    } else {
      setComposerVisibleNodeId(undefined);
    }
  }, [flow.nodes, selectedSeedanceNode?.id]);
  const seedanceComposerLayout = composerLayout({
    viewport: composerViewportSize
  });
  const composerSeedanceNode = useMemo(() => {
    const node = editorDocument.nodes.find((candidate) => candidate.id === composerVisibleNodeId);
    return node?.kind === "seedance" ? node : undefined;
  }, [composerVisibleNodeId, editorDocument.nodes]);
  const composerFlowNode = useMemo(
    () => composerSeedanceNode === undefined ? undefined : flow.nodes.find((node) => node.id === composerSeedanceNode.id),
    [composerSeedanceNode, flow.nodes]
  );
  const composerFloatingPlacement = useMemo(() => {
    if (
      !composerSeedanceNode ||
      !composerFlowNode ||
      seedanceComposerLayout !== "floating"
    ) return undefined;
    return defaultComposerPlacement({
      node: {
        canvasPosition: composerFlowNode.position,
        size: {
          width: composerFlowNode.width ?? composerSeedanceNode.size.width,
          height: composerFlowNode.height ?? composerSeedanceNode.size.height
        }
      },
      flowViewport: flow.viewport,
      canvasViewport: canvasViewportSize,
      card: { width: 520, height: 360 }
    });
  }, [canvasViewportSize, composerFlowNode, composerSeedanceNode, flow.viewport, seedanceComposerLayout]);
  const composerCandidates = useMemo(
    () => composerSeedanceNode ? groupComposerCandidates({
      document: editorDocument,
      knownAssets: availableAssets,
      libraryAssets: composerSearchState.assets,
      query: composerSearchState.query,
      seedanceNodeId: composerSeedanceNode.id
    }) : { canvas: [], library: [] },
    [availableAssets, composerSearchState.assets, composerSearchState.query, composerSeedanceNode, editorDocument]
  );
  const selectComposerCandidate = useCallback((candidate: import("./composer/asset-reference-search.js").ComposerCandidate, query: PromptRange, prompt: PromptContent, seedanceNodeId:string) => {
    if (seedanceNodeId !== composerVisibleNodeId) throw new Error("生成节点已切换，请重新选择素材。");
    const asset = candidate.asset;
    if (candidate.source === "canvas" && (!candidate.nodeId || candidate.edgeId === undefined)) throw new Error("素材选择缺少画布身份，请重新选择。");
    const seedance = documentRef.current.nodes.find((node) => node.id === seedanceNodeId);
    if (seedance?.kind !== "seedance") return;
    const result = addComposerReferenceWithPrompt({
      document: documentRef.current,
      seedanceNodeId,
      asset,
      ...(candidate.source === "canvas" ? {canvasOffer:{nodeId:candidate.nodeId!,edgeId:candidate.edgeId!}} : {}),
      prompt,
      query,
      anchor: {
        x: seedance.position.x - 340,
        y: seedance.position.y + seedance.size.height + 32
      },
      createId: () => crypto.randomUUID()
    });
    rememberAssets([asset]);
    commitDocument(result.document);
    void apiClient.touchAsset(asset.id).then(
      (touched) => rememberAssets([touched]),
      () => touchRetries.add(asset)
    );
  }, [commitDocument, composerVisibleNodeId, rememberAssets, touchRetries]);
  const seedanceComposerContent = useMemo(() => {
    const capabilityValue = capabilities.status === "ready" ? capabilities.value : undefined;
    if (!composerSeedanceNode || !capabilityValue) return undefined;
    return (
      <SeedanceComposer
        document={editorDocument}
        referenceSnapshot={requestPreviewBuilder}
        seedanceNode={composerSeedanceNode}
        capabilities={capabilityValue}
        assets={availableAssets}
        candidates={composerCandidates}
        candidateStatus={composerSearchState.status}
        candidateHasMore={composerSearchState.nextCursor !== undefined}
        onSearchCandidates={searchComposerAssets}
        onLoadMoreCandidates={loadMoreComposerAssets}
        onChange={commitDocument}
        onUndo={undo}
        onRedo={redo}
        onSelectCandidate={selectComposerCandidate}
        onValidate={() => undefined}
        onSubmit={(preview) => submitSeedanceGeneration(preview, composerSeedanceNode.id)}
        onSavePromptTemplate={savePromptTemplate}
        onManagePromptTemplates={() => openProductionLibrary("templates")}
        onClose={() => setComposerVisibleNodeId(undefined)}
      />
    );
  }, [availableAssets, capabilities, commitDocument, composerCandidates, composerSearchState.nextCursor, composerSearchState.status, composerSeedanceNode, editorDocument, requestPreviewBuilder, loadMoreComposerAssets, openProductionLibrary, savePromptTemplate, searchComposerAssets, selectComposerCandidate, submitSeedanceGeneration, undo, redo]);
  const composerNode = useMemo(() => {
    if (
      !composerSeedanceNode ||
      !composerFlowNode ||
      !composerFloatingPlacement ||
      !seedanceComposerContent ||
      seedanceComposerLayout !== "floating"
    ) return undefined;
    const flowNodePlacement = composerPlacementToFlowNode({
      placement: composerFloatingPlacement,
      flowViewport: flow.viewport
    });
    return {
      id: `composer:${composerSeedanceNode.id}`,
      type: "composer" as const,
        position: flowNodePlacement.position,
        width: flowNodePlacement.width,
        height: flowNodePlacement.height,
        style: {
          width: flowNodePlacement.width,
          height: flowNodePlacement.height
        },
        zIndex: 2000,
      draggable: false,
      selectable: false,
      focusable: false,
      connectable: false,
      data: {
        nodeId: composerSeedanceNode.id,
        node: {
          canvasPosition: composerFlowNode.position,
          size: {
            width: composerFlowNode.width ?? composerSeedanceNode.size.width,
            height: composerFlowNode.height ?? composerSeedanceNode.size.height
          }
        },
        flowViewport: flow.viewport,
        canvasViewport: canvasViewportSize,
        layout: seedanceComposerLayout,
        children: seedanceComposerContent
      }
    } as unknown as Node<CanvasFlowNodeData>;
  }, [canvasViewportSize, composerFloatingPlacement, composerFlowNode, composerSeedanceNode, flow.viewport, seedanceComposerContent, seedanceComposerLayout]);
  const flowNodesWithComposer = useMemo(
    () => composerNode === undefined ? renderProjection.nodes : [...renderProjection.nodes, composerNode],
    [composerNode, renderProjection.nodes]
  );
  const selectedTextNode = useMemo(() => {
    const selectedId = flow.nodes.find(({ selected }) => selected)?.id;
    const node = editorDocument.nodes.find(({ id }) => id === selectedId);
    return node?.kind === "text" ? node : undefined;
  }, [editorDocument.nodes, flow.nodes]);
  const selectedMediaNode = selectedIds.length === 1 && selectedMediaNodes.length === 1
    ? selectedMediaNodes[0]
    : undefined;
  const useSelectedMediaAsReference = useCallback((targetNodeId: string) => {
    const source = selectedMediaNode;
    const target = documentRef.current.nodes.find(({ id }) => id === targetNodeId);
    if (!source || target?.kind !== "seedance") return;
    const edge = createCanvasConnection({
      id: crypto.randomUUID(),
      source,
      target,
      existingEdges: documentRef.current.edges
    });
    if (!edge) return;
    commitDocument({ ...documentRef.current, edges: [...documentRef.current.edges, edge] });
  }, [commitDocument, selectedMediaNode]);
  const previewSelectedMedia = useCallback(() => {
    if (!selectedMediaNode) return;
    window.open(`/api/assets/${encodeURIComponent(selectedMediaNode.assetId)}/content`, "_blank", "noopener,noreferrer");
  }, [selectedMediaNode]);
  const connectFromSelectedMedia = useCallback(() => {
    if (!selectedMediaNode) return;
    const handle = document.querySelector<HTMLElement>(`.react-flow__node[data-id="${CSS.escape(selectedMediaNode.id)}"] .react-flow__handle.source`);
    handle?.focus();
  }, [selectedMediaNode]);
  const duplicateSelectedMedia = useCallback(() => {
    if (!selectedMediaNode) return;
    const next = duplicateNodes(documentRef.current, [selectedMediaNode.id]);
    if (next !== documentRef.current) commitDocument(next);
  }, [commitDocument, selectedMediaNode]);
  const deleteSelectedMedia = useCallback(() => {
    if (!selectedMediaNode) return;
    editingDispatch.deleteNodes([selectedMediaNode.id]);
  }, [editingDispatch, selectedMediaNode]);
  const groupBounds = useMemo(() => canvasGroupBounds(editorDocument), [editorDocument]);
  const groupMembersById = useMemo(
    () => new Map(editorDocument.groups.map((group) => [group.id, group.nodeIds] as const)),
    [editorDocument.groups]
  );
  const closeMenu = useCallback(() => setMenu(undefined), []);
  const editNodeFromMenu = useCallback((nodeId: string) => {
    selectNode(nodeId);
    closeMenu();
  }, [closeMenu, selectNode]);
  const copyNodeFromMenu = useCallback((nodeId: string) => {
    const next = duplicateNodes(documentRef.current, [nodeId]);
    if (next !== documentRef.current) commitDocument(next);
    closeMenu();
  }, [closeMenu, commitDocument]);
  const branchNodeFromMenu = useCallback((nodeId: string) => {
    const next = duplicateNodes(documentRef.current, [nodeId]);
    if (next !== documentRef.current) commitDocument(next);
    closeMenu();
  }, [closeMenu, commitDocument]);
  const foldNodeFromMenu = useCallback((nodeId: string) => {
    const next = collapseNodes(documentRef.current, [nodeId]);
    if (next !== documentRef.current) commitDocument(next);
    closeMenu();
  }, [closeMenu, commitDocument]);
  const deleteNodeFromMenu = useCallback((nodeId: string) => {
    editingDispatch.deleteNodes([nodeId]);
    closeMenu();
  }, [closeMenu, editingDispatch]);
  const deleteEdgeFromMenu = useCallback((edgeId: string) => {
    deleteEdgesById([edgeId]);
    closeMenu();
  }, [closeMenu, deleteEdgesById]);
  const openSelectedNodeMenu = useCallback(() => {
    const nodeId = selectedIds[0];
    const bounds = canvasElementRef.current?.getBoundingClientRect();
    if (!nodeId || !bounds || !flowInstance) return;
    setMenu(openNodeMenu({ clientX: bounds.left + bounds.width / 2, clientY: bounds.top + bounds.height / 2 }, nodeId, flowInstance));
  }, [flowInstance, selectedIds]);
  const savePrivateWorkflow = useCallback(() => {
    const clipboard = copyCanvasSelection(documentRef.current, selectedIds);
    if (clipboard.nodes.length === 0) {
      setWorkflowStatus(canvasCopy.selectWorkflowNodes);
      return;
    }
    void apiClient.createWorkflow({
      name: `私有工作流 ${workflows.length + 1}`,
      clipboard
    }).then(
      (workflow) => {
        setWorkflows((current) => mergeWorkflows(current, [workflow]));
        setSelectedWorkflowId(workflow.id);
        setWorkflowStatus(canvasCopy.workflowSaved);
      },
      () => setWorkflowStatus(canvasCopy.workflowLoadFailed)
    );
  }, [selectedIds, workflows.length]);
  const insertPrivateWorkflow = useCallback(() => {
    const workflow = workflows.find(({ id }) => id === selectedWorkflowId);
    if (!workflow || session.saveState.status !== "saved" || workflowInserting) return;
    const anchor = resolveCanvasInsertionPoint();
    if (!anchor) return;
    setWorkflowInserting(true);
    void session.runServerCommand(
      (revision) => apiClient.insertWorkflow({
        workflowId: workflow.id,
        projectId: project.id,
        revision,
        anchor
      }),
      (current, result) => mergeWorkflowInsertion({
        current,
        server: result.canvas.document,
        inserted: result.inserted
      })
    ).then(
      (result) => setWorkflowStatus(result ? canvasCopy.workflowInserted : canvasCopy.workflowLoadFailed),
      () => setWorkflowStatus(canvasCopy.workflowLoadFailed)
    ).finally(() => setWorkflowInserting(false));
  }, [project.id, resolveCanvasInsertionPoint, selectedWorkflowId, session, workflowInserting, workflows]);
  const railTools = useMemo(
    () => canvasTools({ selectedInputCount: selectedInputIds.length, canInsertWorkflow: Boolean(selectedWorkflowId) }),
    [selectedInputIds.length, selectedWorkflowId]
  );
  const addFromToolRail = useCallback((kind: CanvasNodeKind) => {
    createQuickNode(kind);
  }, [createQuickNode]);
  const openAssetsFromToolRail = useCallback(() => {
    openProductionLibrary("assets");
  }, [openProductionLibrary]);
  const openHistoryAssetsFromToolRail = useCallback(() => {
    setGenerationHistoryOpen(true);
  }, []);
  const applyLibraryPromptTemplate = useCallback((template: PromptTemplateView) => {
    if (!selectedSeedanceNode) {
      setWorkflowStatus("请先选择或创建 Seedance 节点。");
      return;
    }
    commitDocument(updateSeedanceSettings(documentRef.current, selectedSeedanceNode.id, { prompt: parseExternalPromptText(template.content, () => crypto.randomUUID()) }));
    setComposerVisibleNodeId(selectedSeedanceNode.id);
    setProductionLibraryOpen(false);
  }, [commitDocument, selectedSeedanceNode]);
  const openBatchFromToolRail = useCallback(() => {
    if (selectedInputIds.length > 0) openSeedancePicker(undefined, selectedInputIds);
  }, [openSeedancePicker, selectedInputIds]);

  return (
    <main className={`studio-canvas-shell${phonePreview ? " studio-canvas-shell--phone-preview" : ""}${tabletDrawer ? ` studio-canvas-shell--drawer-${tabletDrawer}` : ""}${isConnecting ? " studio-canvas-shell--connecting" : ""}${bulkSelectionMode ? " studio-canvas-shell--bulk-selecting" : ""}${composerVisibleNodeId ? " studio-canvas-shell--composer-open" : ""}${desktopSidebars.assets ? "" : " studio-canvas-shell--assets-collapsed"}${desktopSidebars.inspector ? "" : " studio-canvas-shell--inspector-collapsed"}`}>
      <CanvasToolbar
        projectName={project.name}
        saveStatus={canvasSaveStatusLabel(session.saveState)}
        canUndo={history.past.length > 0}
        canRedo={history.future.length > 0}
        search={search}
        onSearchChange={setSearch}
        onLocate={locateSearchResult}
        onUndo={undo}
        onRedo={redo}
        onFiles={chooseFiles}
        uploadVisibility={uploadVisibility}
        onUploadVisibilityChange={setUploadVisibility}
        onAddText={addTextNode}
        onAddScript={addScriptNode}
        onAddSeedance={() => openSeedancePicker()}
        onOpenLibrary={() => openProductionLibrary()}
        canAddSeedance={capabilities.status === "ready"}
        bulkSelectionMode={bulkSelectionMode}
        onToggleBulkSelection={toggleBulkSelection}
        onPhonePreview={() => setPhonePreview((current) => !current)}
        onOpenGenerationHistory={() => navigate("/generations")}
        onToggleAssets={() => setTabletDrawer((current) => current === "assets" ? undefined : "assets")}
        onToggleInspector={() => setTabletDrawer((current) => current === "inspector" ? undefined : "inspector")}
        assetsOpen={tabletDrawer === "assets"}
        inspectorOpen={tabletDrawer === "inspector"}
      />
      {session.saveState.status === "conflict" ? (
        <aside className="studio-conflict" role="alert">
          <span>{session.saveState.upgradeRequired ? canvasCopy.canvasUpgradeRequired : "服务器画布已更新，本地版本尚未被覆盖。"}</span>
          <button type="button" onClick={session.exportLocal}>导出本地恢复副本</button>
          <button type="button" onClick={() => void session.loadServer()}>{canvasCopy.loadServer}</button>
          <button type="button" onClick={() => void session.copyLocalToNewProject()}>{canvasCopy.copyLocal}</button>
        </aside>
      ) : null}
      <CanvasRecoveryNotice recovery={session.recoveryState} listingError={session.recoveryError} onExport={session.exportLocal} onRetry={session.retry} />
      <aside className="studio-touch-notice"><details>
        <summary>{canvasCopy.recoveryDrafts}（{session.recoveryDrafts.length}）</summary>
        <button type="button" onClick={() => void session.refreshRecoveryDrafts()}>{canvasCopy.refreshDrafts}</button>
        <button type="button" onClick={session.exportLocal}>{canvasCopy.exportLocal}</button>
        {session.recoveryDrafts.map((draft, index) => (
          <div key={`${draft.writerId ?? "legacy"}:${draft.draftId ?? "legacy"}`}>
            <span>副本 {index + 1} · {new Date(draft.savedAt).toLocaleString("zh-CN")}</span>
            <button type="button" disabled={session.saveState.status === "saving"} onClick={() => session.restoreDraft(draft)}>{canvasCopy.restoreDraft}</button>
          </div>
        ))}
      </details></aside>
      {session.saveState.status === "offline_draft" || session.saveState.status === "save_failed" ? (
        <button className="studio-retry" type="button" onClick={session.retry}>{canvasCopy.retrySave}</button>
      ) : null}
      {touchFailures.map((asset) => (
        <aside className="studio-touch-notice" role="status" key={asset.id}>
          <span>{asset.displayName}{canvasCopy.touchFailure}</span>
          <button type="button" onClick={() => void touchRetries.retry(asset.id)}>{canvasCopy.retry}</button>
        </aside>
      ))}
      <div className="studio-canvas-workspace">
        <AssetPanel controller={assetPanel} searchInputRef={assetSearchInputRef} />
        <section
          ref={canvasElementRef}
          className={`studio-canvas${isDragActive ? " studio-canvas--drop-active" : ""}`}
          aria-label={canvasCopy.projectCanvas}
          onDragOver={onDragOver}
          onDragLeave={() => setIsDragActive(false)}
          onDrop={onDrop}
          onDoubleClickCapture={(event) => {
            const target = event.target as Element;
            if (
              !target.closest(".react-flow__pane") ||
              target.closest(".react-flow__node, .studio-upload-queue, .studio-selection-toolbar, .studio-batch-connection-hint, .studio-selection-connector") ||
              !flowInstance
            ) return;
            event.preventDefault();
            const menu = openCanvasMenuAt({ x: event.clientX, y: event.clientY }, flowInstance);
            resolveCanvasInsertionPoint(menu.flow);
            setMenu(menu);
          }}
        >
          <ReactFlow<Node<CanvasFlowNodeData>, Edge<CanvasFlowEdgeData>>
            nodes={flowNodesWithComposer}
            edges={renderProjection.edges}
            viewport={flow.viewport}
            nodeTypes={canvasNodeTypes}
            onMouseDownCapture={(event) => {
              if (event.button !== 1 || !(event.target instanceof Element)) return;
              const target = event.target;
              if (
                !target.closest(".react-flow__pane, .react-flow__node, .react-flow__edge") ||
                target.closest(
                  "a[href], button, input, textarea, select, label, summary, [contenteditable], [role='button'], [role='textbox'], video[controls], audio[controls], " +
                  // XYFlow marks node/edge wrappers nopan for left-button
                  // behavior, but explicitly allows middle-button panning.
                  ".nodrag, .nopan:not(.react-flow__node):not(.react-flow__edge), .nowheel, .react-flow__panel, .react-flow__node-composer"
                )
              ) return;
              // Cancel native middle-button autoscroll before d3 handles the
              // mousedown. Keep propagation so XYFlow still performs the pan.
              event.preventDefault();
            }}
            onNodesChange={onNodesChange}
            onEdgesChange={onEdgesChange}
            onConnect={onConnect}
            onConnectStart={(event, parameters) => {
              setIsConnecting(true);
              const sourceNodeIds = parameters.handleType === "source" && parameters.nodeId
                ? connectionSourceIds(parameters.nodeId)
                : undefined;
              connectionStartRef.current = sourceNodeIds;
              if (sourceNodeIds && sourceNodeIds.length > 1 && "clientX" in event) {
                setBatchConnectionPreview({
                  sourceNodeIds,
                  client: { x: event.clientX, y: event.clientY }
                });
              }
            }}
            onConnectEnd={(event) => {
              setIsConnecting(false);
              const sourceNodeIds = connectionStartRef.current;
              connectionStartRef.current = undefined;
              setBatchConnectionPreview(undefined);
              if (!sourceNodeIds || sourceNodeIds.length === 0 || !flowInstance) return;
              const client = "changedTouches" in event
                ? (() => {
                    const touch = event.changedTouches.item(0);
                    return touch ? { x: touch.clientX, y: touch.clientY } : undefined;
                  })()
                : { x: event.clientX, y: event.clientY };
              if (!client) return;
              const element = document.elementFromPoint(client.x, client.y);
              if (
                !element?.closest(".react-flow__pane") ||
                element.closest(".react-flow__node, .studio-upload-queue, .studio-selection-toolbar, .studio-batch-connection-hint")
              ) return;
              const flow = flowInstance.screenToFlowPosition(client);
              resolveCanvasInsertionPoint(flow);
              setGenerationPicker({
                sourceNodeIds,
                client,
                flow
              });
            }}
            onInit={setFlowInstance}
            onPaneClick={(event) => {
              setComposerVisibleNodeId(undefined);
              if (!flowInstance) return;
              resolveCanvasInsertionPoint(flowInstance.screenToFlowPosition({
                x: event.clientX,
                y: event.clientY
              }));
            }}
            onNodeClick={(_, node) => {
              if (node.type === "composer") return;
              if (bulkSelectionModeRef.current) {
                const canvasNode = node.data.canvasNode;
                if (canvasNode.kind !== "image" && canvasNode.kind !== "video" && canvasNode.kind !== "audio") return;
                const selected = new Set(flowRef.current.nodes
                  .filter((candidate) => candidate.selected)
                  .map((candidate) => candidate.id));
                if (selected.has(node.id)) selected.delete(node.id);
                else selected.add(node.id);
                setFlow((current) => {
                  const next = {
                    ...current,
                    nodes: current.nodes.map((candidate) => ({
                      ...candidate,
                      selected: selected.has(candidate.id)
                    }))
                  };
                  flowRef.current = next;
                  return next;
                });
                return;
              }
              if (node.data.canvasNode.kind === "seedance") {
                setComposerVisibleNodeId(node.id);
              } else {
                setComposerVisibleNodeId(undefined);
              }
            }}
            onPaneContextMenu={(event) => {
              event.preventDefault();
              if (!flowInstance) return;
              const menu = openCanvasMenu(event, flowInstance);
              resolveCanvasInsertionPoint(menu.flow);
              setMenu(menu);
            }}
            onNodeContextMenu={(event, node) => {
              event.preventDefault();
              selectNode(node.id);
              if (flowInstance) setMenu(openNodeMenu(event, node.id, flowInstance));
            }}
            onEdgeContextMenu={(event, edge) => {
              event.preventDefault();
              if (!flowInstance) return;
              const current = flowRef.current;
              updateFlow({
                ...current,
                edges: current.edges.map((candidate) => ({
                  ...candidate,
                  selected: candidate.id === edge.id
                }))
              }, false);
              setMenu(openEdgeMenu(event, edge.id, flowInstance));
            }}
            onViewportChange={(viewport) => updateFlow(withFlowViewport(flowRef.current, viewport), false)}
            onMoveEnd={(_, viewport) => updateFlow(
              withFlowViewport(flowRef.current, viewport),
              shouldPersistCanvasViewChange()
            )}
            deleteKeyCode={null}
            selectionOnDrag={canvasTool === "select"}
            selectionMode={SelectionMode.Partial}
            nodesDraggable={!bulkSelectionMode}
            panOnDrag={canvasTool === "pan" ? [0, 1, 2] : [1, 2]}
            multiSelectionKeyCode="Shift"
            minZoom={0.05}
            maxZoom={4}
            zoomOnDoubleClick={false}
            {...(batchConnectionPreview ? { connectionLineStyle: { opacity: 0 } } : {})}
            proOptions={{ hideAttribution: true }}
          >
            {composerVisibleNodeId === undefined ? <MiniMap /> : null}
            <Background variant={BackgroundVariant.Dots} gap={20} size={1} color="#363e4c" />
          </ReactFlow>
          {composerSeedanceNode && seedanceComposerContent && seedanceComposerLayout === "bottom-sheet" ? (
            <SeedanceComposerOverlay
              nodeId={composerSeedanceNode.id}
              node={{
                canvasPosition: composerFlowNode?.position ?? composerSeedanceNode.position,
                size: {
                  width: composerFlowNode?.width ?? composerSeedanceNode.size.width,
                  height: composerFlowNode?.height ?? composerSeedanceNode.size.height
                }
              }}
              flowViewport={flow.viewport}
              canvasViewport={canvasViewportSize}
              layout="bottom-sheet"
            >
              {seedanceComposerContent}
            </SeedanceComposerOverlay>
          ) : null}
          {selectionConnector && composerVisibleNodeId === undefined ? (
            <SelectionConnectionOverlay
              layout={selectionConnector}
              onPointerDown={beginSelectionConnection}
              onPointerUp={finishSelectionConnection}
              onPointerCancel={cancelSelectionConnection}
              onKeyboardOpen={openSelectionSeedancePicker}
            />
          ) : null}
          {batchConnectionPreview ? (
            <BatchConnectionPreview
              canvasElement={canvasElementRef.current}
              sourceNodeIds={batchConnectionPreview.sourceNodeIds}
              client={batchConnectionPreview.client}
            />
          ) : null}
          {selectedMediaNodes.length >= 2 ? (
            <output className="studio-batch-connection-hint" role="status" aria-label="批量连线提示">
              已选 {selectedMediaNodes.length} 个素材，{canvasCopy.batchConnectionHint}
            </output>
          ) : null}
          {generationPicker && capabilities.status === "ready" ? (
            <section
              className="studio-generation-picker"
              role="dialog"
              aria-modal="true"
              aria-label="选择 Seedance 模型"
              style={generationPickerPosition({
                client: generationPicker.client,
                viewport: { width: window.innerWidth, height: window.innerHeight }
              })}
            >
              <header>
                <strong>选择 Seedance 模型</strong>
                <button type="button" aria-label="关闭 Seedance 模型选择" onClick={() => setGenerationPicker(undefined)}>×</button>
              </header>
              <p>{generationPicker.sourceNodeIds === undefined
                ? "选择模型后会新建一个视频生成节点。"
                : generationPicker.sourceNodeIds.length > 1
                  ? `已选 ${generationPicker.sourceNodeIds.length} 个素材；选择模型后会新建并自动连接。`
                  : "已连接素材；选择模型后会新建并自动连线。"}</p>
              <div>
                {capabilities.value.models.map(({ id }) => (
                  <button
                    key={id}
                    className="foundation-button foundation-button--primary"
                    type="button"
                    onClick={() => createSeedanceFromPicker(id)}
                  >
                    {seedanceModelLabel(id)}
                  </button>
                ))}
              </div>
            </section>
          ) : null}
          {selectedMediaNode && !bulkSelectionMode ? (
            <NodeActionStrip
              nodeId={selectedMediaNode.id}
              assetName={assetsById[selectedMediaNode.assetId]?.displayName ?? selectedMediaNode.kind}
              seedanceTargets={editorDocument.nodes
                .filter((node): node is Extract<CanvasDocument["nodes"][number], { kind: "seedance" }> => node.kind === "seedance")
                .map((node) => ({ id: node.id, label: seedanceModelLabel(node.settings.model) }))}
              onPreview={previewSelectedMedia}
              onDuplicate={duplicateSelectedMedia}
              onStartConnection={connectFromSelectedMedia}
              onDelete={deleteSelectedMedia}
              onUseAsReference={useSelectedMediaAsReference}
            />
          ) : null}
          <input
            ref={quickMediaInputRef}
            className="studio-quick-media-input"
            type="file"
            aria-label="选择快速添加素材"
            accept={quickMediaAdd?.kind === "image" ? "image/*" : quickMediaAdd?.kind === "video" ? "video/*" : "audio/*"}
            onChange={chooseQuickMedia}
          />
          <div
            className="studio-group-overlays"
            aria-label={canvasCopy.canvasGroups}
          >
            <div
              className="studio-group-overlays__viewport"
              style={{
                transform: `translate(${flow.viewport.x}px, ${flow.viewport.y}px) scale(${flow.viewport.zoom})`
              }}
            >
              {groupBounds.map((group) => {
                const members = groupMembersById.get(group.id) ?? [];
                const selected = members.length > 0 && members.length === selectedIds.length
                  && members.every((id) => selectedIds.includes(id));
                return <CanvasGroupOverlay key={group.id} group={group} zoom={flow.viewport.zoom} selected={selected}
                  onSelect={selectGroup} onUngroup={ungroupGroup} onDragStart={startGroupDrag}
                  onDragMove={moveGroupDrag} onDragEnd={finishGroupDrag} onDragCancel={cancelGroupDrag} />;
              })}
            </div>
          </div>
          <CanvasToolRail
            tools={railTools}
            activeTool={activeCanvasToolPopover}
            onOpenTool={setActiveCanvasToolPopover}
            onSetCanvasTool={setCanvasTool}
            onAdd={addFromToolRail}
            onBatch={openBatchFromToolRail}
            onSaveWorkflow={savePrivateWorkflow}
            onInsertWorkflow={insertPrivateWorkflow}
            canInsertWorkflow={Boolean(selectedWorkflowId) && session.saveState.status === "saved" && !workflowInserting}
            workflows={workflows}
            selectedWorkflowId={selectedWorkflowId}
            onSelectWorkflow={setSelectedWorkflowId}
            onOpenAssets={openAssetsFromToolRail}
            onOpenHistoryAssets={openHistoryAssetsFromToolRail}
          />
          <div className="studio-canvas-corner-actions">
            {selectedIds[0] ? (
              <button className="studio-selected-node-menu" type="button" aria-label={canvasCopy.openSelectedNodeMenu} onClick={openSelectedNodeMenu}>{canvasCopy.openSelectedNodeMenu}</button>
            ) : null}
          </div>
          {workflowStatus ? <output className="studio-workflow-status" role="status" aria-live="polite">{workflowStatus}</output> : null}
          {menu ? (
            <CanvasContextMenu
              state={menu}
              onClose={closeMenu}
              onCreate={(kind, state) => {
                if (state.kind !== "blank") return;
                resolveCanvasInsertionPoint(state.flow);
                createQuickNode(kind, state.flow);
                closeMenu();
              }}
              onEdit={editNodeFromMenu}
              onCopy={copyNodeFromMenu}
              onBranch={branchNodeFromMenu}
              onToggleFold={foldNodeFromMenu}
              onDelete={deleteNodeFromMenu}
              onDeleteEdge={deleteEdgeFromMenu}
              isFolded={(nodeId) => documentRef.current.nodes.find((node) => node.id === nodeId)?.collapsed ?? false}
            />
          ) : null}
          {isDragActive ? <div className="studio-drop-zone">{canvasCopy.dropMedia}</div> : null}
          <UploadQueue controller={uploadQueue} />
        </section>
        {selectedTextNode ? (
          <TextNodeInspector onUndo={undo} onRedo={redo}
            document={editorDocument}
            textNode={selectedTextNode}
            onChange={commitDocument}
            onSavePromptTemplate={savePromptTemplate}
            onManagePromptTemplates={() => openProductionLibrary("templates")}
          />
        ) : (
          <aside className="node-inspector node-inspector--empty" aria-label={canvasCopy.inspector}>
            {capabilities.status === "error"
              ? "Seedance 参数加载失败。画布与上传仍可继续使用。"
              : canvasCopy.selectSeedance}
          </aside>
        )}
        <MobileNodeList onUndo={undo} onRedo={redo}
          document={editorDocument}
          {...(selectedIds[0] ? { selectedId: selectedIds[0] } : {})}
          onSelect={selectNode}
          onChange={commitDocument}
        />
        <button
          className="studio-sidebar-toggle studio-sidebar-toggle--assets"
          type="button"
          aria-label={desktopSidebars.assets ? canvasCopy.collapseAssets : canvasCopy.expandAssets}
          aria-expanded={desktopSidebars.assets}
          onClick={() => toggleDesktopSidebar("assets")}
        >{desktopSidebars.assets ? "‹" : "›"}</button>
        <button
          className="studio-sidebar-toggle studio-sidebar-toggle--inspector"
          type="button"
          aria-label={desktopSidebars.inspector ? canvasCopy.collapseInspector : canvasCopy.expandInspector}
          aria-expanded={desktopSidebars.inspector}
          onClick={() => toggleDesktopSidebar("inspector")}
        >{desktopSidebars.inspector ? "›" : "‹"}</button>
      </div>
      {composerVisibleNodeId === undefined && !(selectedMediaNode && !bulkSelectionMode) ? (
        <SelectionToolbar
          selectedCount={bulkSelectionMode ? 0 : selectedIds.length}
          selectedCollapsedCount={bulkSelectionMode ? 0 : selectedCollapsedCount}
          bulkMediaCount={selectedMediaNodes.length}
          bulkSelectionMode={bulkSelectionMode}
          seedanceTargets={seedanceTargets}
          onCreateBulkSeedance={createBulkSeedance}
          onConnectBulkToSeedance={connectBulkToSeedance}
          onAlign={(alignment: Alignment) => applySelectionCommand((document, ids) => alignNodes(document, ids, alignment))}
          onDistribute={(axis: DistributionAxis) => applySelectionCommand((document, ids) => distributeNodes(document, ids, axis))}
          onGroup={() => applySelectionCommand(groupNodes)}
          onUngroup={() => applySelectionCommand((document, ids) => ungroupNodes(document, document.groups.filter((group) => group.nodeIds.some((id) => ids.includes(id))).map((group) => group.id)))}
          onFold={() => applySelectionCommand(collapseNodes)}
          onDuplicate={() => applySelectionCommand(duplicateNodes)}
          onDelete={() => applySelectionCommand(deleteNodes)}
        />
      ) : null}
      <ProductionLibraryDialog
        open={productionLibraryOpen}
        initialTab={productionLibraryInitialTab}
        onClose={() => setProductionLibraryOpen(false)}
        onAssetImported={acceptImportedAsset}
        onPromptImported={acceptImportedPrompt}
        onAddAsset={(asset) => {
          placePanelAsset(asset);
          setProductionLibraryOpen(false);
        }}
        onApplyTemplate={applyLibraryPromptTemplate}
        viewerRole={viewerRole}
      />
      <GenerationAssetHistoryDialog
        open={generationHistoryOpen}
        onClose={() => setGenerationHistoryOpen(false)}
        onAddAsset={placePanelAsset}
      />
    </main>
  );
}

export function ProductionCanvasPage() {
  const { projectId } = useParams();
  const { state: authState, recoverAuthentication } = useAuth();
  const [state, setState] = useState<PageState>({ status: "loading" });

  const load = useCallback(async () => {
    if (!projectId) {
      setState({ status: "not-found" });
      return;
    }
    setState({ status: "loading" });
    try {
      const [project, canvas] = await Promise.all([
        apiClient.getProject(projectId),
        apiClient.getCanvas(projectId)
      ]);
      setState({ status: "ready", project, canvas });
    } catch (error) {
      if (recoverAuthentication(error)) return;
      if (error instanceof TypeError) setState({ status: "offline" });
      else if (error instanceof ApiClientError && error.status === 404) setState({ status: "not-found" });
      else setState({ status: "error" });
    }
  }, [projectId, recoverAuthentication]);

  useEffect(() => {
    void load();
  }, [load]);

  if (state.status === "ready" && authState.status === "authenticated") {
    return <CanvasEditor project={state.project} canvas={state.canvas} ownerId={authState.user.id} viewerRole={authState.user.role} />;
  }
  return (
    <main className="studio-load-state" role={state.status === "loading" ? undefined : "alert"}>
      <h1>{state.status === "loading" ? canvasCopy.openingProject : state.status === "not-found" ? canvasCopy.projectNotFound : state.status === "offline" ? canvasCopy.networkUnavailable : canvasCopy.projectOpenFailed}</h1>
      {state.status !== "loading" ? <button className="foundation-button foundation-button--primary" type="button" onClick={() => void load()}>{canvasCopy.retry}</button> : null}
    </main>
  );
}
